import { type LinkColor } from '@model-framework/link/LinkColor';
import { type LinkLineStyle } from '@model-framework/link/LinkLineStyle/LinkLineStyle';
import { type LinkMarkStyle } from '@model-framework/link/LinkMarkStyle/LinkMarkStyle';
import { type FontSize } from '@model-framework/text/FontSize';
import { type ThemeColor } from '@view-model/models/common/color/ThemeColor';
import { type TextAlign } from '@view-model/models/common/text-align';
import { type AlignType } from '../AlignMenu';
import { selectMenuGroups } from '../../MultiSelectionMenu';
import { RightClickMenu } from '@framework/ui/elements/RightClickMenu';
import { type Position } from '@view-model/models/common/types/ui';
import { useRef } from 'react';
import { TextSizeMenuButton } from '@view-model/models/sticky/StickyNodeView/components/NodeRightClickMenuItem/TextSizeMenuButton';
import { useHandler } from '@framework/hooks';
import { ColorMenuButton } from '@view-model/models/sticky/StickyNodeView/components/NodeRightClickMenuItem';
import type { StickyZoneShape } from '@view-model/models/sticky/StickyZoneView';
import { ZoneShapeMenuButton } from '@view-model/models/sticky/StickyZoneView/components/StickyZoneRightClickMenuItem';
import { TextAlignMenuButton } from '@view-model/models/sticky/StickyNodeView/components/NodeRightClickMenuItem/TextAlignMenuButton';
import {
  LinkColorMenuButton,
  LinkLineStyleMenuButton,
  LinkMarkStyleMenuButton,
} from '@view-model/models/sticky/StickyLink/components/LinkRightClickMenuItem';
import { AlignMenuButton } from './AlignMenuButton';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  isNodeSelected: boolean;
  isMultipleNodeSelected: boolean;
  isLinkSelected: boolean;
  isZoneSelected: boolean;
  mousePosition: Position | null;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(shape: StickyZoneShape): void;
  onGroupSelectedElements(): void;
  onLinkLineStyleSelected(lineStyle: LinkLineStyle): void;
  onLinkMarkStyleSelected(markStyle: LinkMarkStyle): void;
  onReverseLinks(): void;
  onRemoveLinks(): void;
  onLinkColorSelected(color: LinkColor): void;
  onAlignSelected(alignType: AlignType, interval: number): void;
};

type RightClickMenuButtonRef = {
  closePopup: () => void;
};

export const MultiSelectionRightClickMenu: React.FC<Props> = ({
  isOpen,
  isNodeSelected,
  isMultipleNodeSelected,
  isLinkSelected,
  isZoneSelected,
  mousePosition,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onGroupSelectedElements,
  onLinkLineStyleSelected,
  onLinkMarkStyleSelected,
  onReverseLinks,
  onRemoveLinks,
  onLinkColorSelected,
  onAlignSelected,
}: Props) => {
  const { t } = useTranslation();
  const textSizeMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const colorMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const textAlignMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const zoneShapeMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const lineStyleMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const markStyleMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const linkColorMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const verticalAlignMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const horizontalAlignMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);

  const menuGroups = selectMenuGroups({
    isNodeSelected,
    isMultipleNodeSelected,
    isLinkSelected,
    isZoneSelected,
  });

  const filteredMenuGroups = menuGroups.filter((row) => row.length > 0);

  const closeOtherRightClickSubMenu = useHandler((menuType: string): void => {
    if (menuType !== 'textSize') textSizeMenuButtonRef.current?.closePopup();
    if (menuType !== 'color') colorMenuButtonRef.current?.closePopup();
    if (menuType !== 'textAlign') textAlignMenuButtonRef.current?.closePopup();
    if (menuType !== 'zoneShape') zoneShapeMenuButtonRef.current?.closePopup();
    if (menuType !== 'lineStyle') lineStyleMenuButtonRef.current?.closePopup();
    if (menuType !== 'markStyle') markStyleMenuButtonRef.current?.closePopup();
    if (menuType !== 'linkColor') linkColorMenuButtonRef.current?.closePopup();
    if (menuType !== 'verticalAlign') verticalAlignMenuButtonRef.current?.closePopup();
    if (menuType !== 'horizontalAlign') horizontalAlignMenuButtonRef.current?.closePopup();
  });

  // すべてのメニューグループが空の場合は RightClickMenu を開かない
  if (filteredMenuGroups.length === 0) {
    return null;
  }

  return (
    <RightClickMenu isOpen={isOpen} mousePosition={mousePosition}>
      {filteredMenuGroups.map((group, index) => {
        return (
          <div key={`group-${index}`}>
            {group.map((menuItem) => {
              switch (menuItem) {
                case 'FontSize':
                  return (
                    <TextSizeMenuButton
                      key="fontSize"
                      ref={textSizeMenuButtonRef}
                      onChangeTextSize={onFontSizeSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'ThemeColor':
                  return (
                    <ColorMenuButton
                      key="themeColor"
                      ref={colorMenuButtonRef}
                      onChangeColor={onThemeColorSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'TextAlign':
                  return (
                    <TextAlignMenuButton
                      key="textAlign"
                      ref={textAlignMenuButtonRef}
                      onChangeTextAlign={onTextAlignSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'ZoneShape':
                  return (
                    <ZoneShapeMenuButton
                      key="zoneShape"
                      ref={zoneShapeMenuButtonRef}
                      onChangeShape={onZoneShapeSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'FontStrikethrough':
                  return (
                    <div
                      key="fontStrikethrough"
                      onClick={onToggleFontStrikethrough}
                      className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200"
                    >
                      {t('canvas.menu.strikethrough')}
                    </div>
                  );
                case 'LinkLineStyle':
                  return (
                    <LinkLineStyleMenuButton
                      key="linkLineStyle"
                      ref={lineStyleMenuButtonRef}
                      onLineStyleSelected={onLinkLineStyleSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'LinkMarkStyle':
                  return (
                    <LinkMarkStyleMenuButton
                      key="linkMarkStyle"
                      ref={markStyleMenuButtonRef}
                      onMarkStyleSelected={onLinkMarkStyleSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );

                case 'ReverseLink':
                  return (
                    <div
                      key="reverseLink"
                      onClick={onReverseLinks}
                      className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200"
                    >
                      {t('canvas.menu.reverseLink')}
                    </div>
                  );
                case 'LinkColor':
                  return (
                    <LinkColorMenuButton
                      key="linkColor"
                      ref={linkColorMenuButtonRef}
                      onChangeColor={onLinkColorSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );

                case 'RemoveMultiSelectedLinks':
                  return (
                    <div
                      key="removeMultiSelectedLinks"
                      onClick={onRemoveLinks}
                      className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200"
                    >
                      {t('canvas.menu.multiSelection.removeLinks')}
                    </div>
                  );

                case 'GroupStickyElements':
                  return (
                    <div
                      key="groupStickyElements"
                      onClick={onGroupSelectedElements}
                      className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200"
                    >
                      {t('canvas.menu.group')}
                    </div>
                  );
                case 'HorizontalAlignMenu':
                  return (
                    <AlignMenuButton
                      key="horizontalAlignMenu"
                      alignType="horizontal"
                      ref={horizontalAlignMenuButtonRef}
                      onSelected={onAlignSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                case 'VerticalAlignMenu':
                  return (
                    <AlignMenuButton
                      key="verticalAlignMenu"
                      alignType="vertical"
                      ref={verticalAlignMenuButtonRef}
                      onSelected={onAlignSelected}
                      onOpenSubMenu={closeOtherRightClickSubMenu}
                    />
                  );
                default:
                  return null;
              }
            })}
            {index !== filteredMenuGroups.length - 1 && (
              <div key={`separator-${index}`} className="my-4 border border-gray-400" />
            )}
          </div>
        );
      })}
    </RightClickMenu>
  );
};
